import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { formatDuration, formatHours } from "../../lib/time";
import { useAgents, useDashKpis, useWorkflows } from "./queries";

interface KpiCard {
  label: string;
  value?: string;
  sub: string;
  to: string | null;
  delta?: number;
  invert?: boolean;
  highlight?: boolean;
}

export function KpiCards({ dateParams }: { dateParams?: { start_date?: string; end_date?: string } }) {
  const kpis = useDashKpis(dateParams);
  const agents = useAgents();
  const workflows = useWorkflows();
  const nav = useNavigate();

  const k = kpis.data?.global;

  const baseCards: KpiCard[] = [
    {
      label: "CONTACTS DIALLED",
      value: (k?.unique_contacts ?? k?.total_calls)?.toLocaleString("en-IN"),
      sub: "Unique contacts dialled",
      to: "/analytics",
    },
    { label: "MINUTES SERVED", value: k ? formatHours(k.total_credits_used) : undefined, sub: "Billable minutes", to: "/analytics" },
    { label: "AVG. CONV. DURATION", value: k ? formatDuration(k.total_connected_calls > 0 ? (k.total_credits_used * 60) / k.total_connected_calls : 0) : undefined, sub: "Per connected call", to: "/analytics" },
    { label: "AGENTS", value: String((agents.data?.length ?? 0) + (workflows.data?.length ?? 0)),
      sub: `${agents.data?.length ?? 0} single · ${workflows.data?.length ?? 0} workflows`, to: null },
  ];

  // One KPI card per agent that has a primary outcome configured.
  const perAgentOutcomes = k?.per_agent_primary_outcomes ?? [];
  const outcomeCards: KpiCard[] = perAgentOutcomes.map((po) => ({
    label: po.primary_outcome_name.toUpperCase(),
    value: po.count.toLocaleString("en-IN"),
    sub: po.agent_name,
    to: "/analytics",
    highlight: true,
  }));

  const cards = [...baseCards, ...outcomeCards];

  // Dynamic grid: 4 base columns + 1 per outcome card
  const gridCols = Math.min(cards.length, 6);
  const gridClass = gridCols <= 4
    ? "grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
    : gridCols === 5
      ? "grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5"
      : "grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6";

  return (
    <div className={gridClass}>
      {cards.map((c) => (
        <button
          key={c.label + c.sub}
          onClick={() => c.to && nav(c.to)}
          disabled={!c.to}
          className={[
            "kpi-card p-4 text-left transition-[box-shadow,transform] duration-normal",
            "enabled:hover:-translate-y-0.5 enabled:hover:shadow-card-hover disabled:cursor-default",
            c.highlight ? "ring-1 ring-amber-400/40 dark:ring-amber-500/30" : "",
          ].join(" ")}
        >
          <div className="flex items-center gap-1.5">
            <p className="font-mono text-[9px] font-semibold uppercase tracking-widest text-ink-3">{c.label}</p>
            {c.highlight && (
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400"
                title="Primary outcome tracking active"
              />
            )}
          </div>
          {kpis.isLoading && !c.value ? (
            <div className="mt-2 h-8 w-24 animate-pulse rounded bg-soft" />
          ) : (
            <p className="mt-1.5 font-mono text-3xl font-semibold tracking-tight tabular-nums text-ink">{c.value ?? "—"}</p>
          )}
          {c.delta !== undefined ? (
            <Badge tone={(c.invert ? c.delta < 0 : c.delta > 0) ? "success" : "danger"} className="mt-2">
              {c.delta > 0 ? "▲" : "▼"} {Math.abs(c.delta)}% vs last period
            </Badge>
          ) : (
            <p className="mt-1 font-mono text-[10px] text-ink-3">{c.sub}</p>
          )}
        </button>
      ))}
    </div>
  );
}
